import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { VolunteerSpecialistPool, VolunteerTopic } from '@interfaces';
import { DomainDataService, TrimesterService } from '../../../../../services';
import { VolunteeringAreaService } from '../../../volunteering-area.service';
import { VolunteeringSpecialistsService } from '../../volunteering-specialists.service';
import { getSetup } from './grid/get-setup';

@Component({
	selector: 'specialist-pool-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './specialist-pool.part.html',
})
export class SpecialistPoolViewPart implements OnInit {

	private readonly util = inject(UtilityService);
	public readonly areaService = inject(VolunteeringAreaService);
	private readonly pageService = inject(VolunteeringSpecialistsService);
	private readonly domainDataService = inject(DomainDataService);
	private readonly dialogService = inject(DialogService);
	private readonly trimesterService = inject(TrimesterService);

	public setup = computed(() => {
		// change the columns when the selected trimester changes
		const selectedTrimester = this.trimesterService.selectedTrimester();
		return getSetup(this.areaService, this.util, this.domainDataService, this.dialogService, this.areaService.app);
	});

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	ngOnInit() {
		this.areaService.getVolunteeringData();
	}

	public specialistPool = computed<VolunteerSpecialistPool[]>(() => {
		const data = this.areaService.volunteeringData();
		if (!data) return [];

		const approvedTopicsCountMap = this.getApprovedTopicsCount(data.volunteerTopics);

		const signUpsCountByTrimesterMap = this.areaService.specialistSignUpsCountMapByTrimester;

		return data.volunteers
			.filter(v => v.isInClassApproved)
			.map(v => {
				return {
					volunteerId: v.volunteerId,
					person: v.person,
					countOfApprovedTopcis: approvedTopicsCountMap[v.volunteerId] || 0,
					signUpsCount: signUpsCountByTrimesterMap[v.volunteerId] || { currentTrimester: 0, nextTrimester: 0 },
					updatedUTC: v.updatedUTC,
					volunteerStatusId: v.volunteerStatusId,
					lastContactUTC: v.lastContactUTC,
					lastContactStaffId: v.lastContactStaffId,
					assignedToStaffId: v.assignedToStaffId,
					attendedOrientation: v.attendedOrientation,
					hasEnVideo: v.hasEnVideo,
					hasEsVideo: v.hasEsVideo,
					handbookSigned: v.handbookSigned,
					mediaConsentSigned: v.mediaConsentSigned,
					agreementsSigned: v.agreementsSigned,
					interviewDateUTC: v.interviewDateUTC,
					interviewScore: v.interviewScore,
				};
			})
	});

	private getApprovedTopicsCount(topics: VolunteerTopic[]) {
		return topics.filter(t => t.isApproved).reduce((map, topic) => {
			const key = topic.volunteerId;
			map[key] = (map[key] || 0) + 1;
			return map;

		}, {} as Record<number, number>);
	};
}